import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, CreditCard, Loader2, Plus } from 'lucide-react';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PnrSourceMark } from '@/components/provenance/pnr-source-mark';
import { cn } from '@/lib/cn';
import { LockConflictError } from '@/lib/locks/types';
import { useIssueGroupVoucher } from '@/lib/disruptions/use-disruptions';
import type { CareBoard, CareOutcomeKind } from '@/lib/disruptions/types';
import { priorityLabel } from '../labels';
import { policyReasonText } from '@/lib/policy-reason/policy-reason';
import {
  careLaneReason,
  formatEntitlement,
  formatMinor,
  type CareDeskRow,
  type CareLane,
} from './care-desk-model';
import { CareOutcomeDialog } from './care-outcome-dialog';
import { CareRedeemDialog } from './care-redeem-dialog';
import { CareTopUpDialog, type CareTopUpPrefill } from './care-top-up-dialog';

// The care desk's right half (feat/care-desk K5, formal dialogs K6): resolve ONE
// PNR, with context. Header says who and why; the body states the entitlement
// (with its policy provenance) and the card's honest money truth (never a
// fabricated $0.00); the alternatives ladder sits at the bottom, once — quiet
// until needed, opening the formal outcome dialog (K6) with its rung pre-
// selected. Solving here auto-advances the queue (the parent re-selects the
// next open PNR when this one leaves the lane).

const LANE_BADGE: Partial<Record<CareLane, { labelKey: string; variant: BadgeVariant }>> = {
  FAILED: { labelKey: 'resolve.lane.failed', variant: 'error' },
  NEEDS_ISSUE: { labelKey: 'resolve.lane.needsIssue', variant: 'warning' },
  ATTENTION: { labelKey: 'resolve.lane.attention', variant: 'info' },
  BLOCKED_KYC: { labelKey: 'resolve.lane.blockedKyc', variant: 'info' },
  HANDLED_OUTSIDE: { labelKey: 'resolve.lane.handledOutside', variant: 'neutral' },
  ISSUED: { labelKey: 'resolve.lane.issued', variant: 'success' },
};

const CARD_STATUS_VARIANT: Record<string, BadgeVariant> = {
  ACTIVE: 'success',
  ISSUED: 'success',
  PENDING: 'info',
  FAILED: 'error',
  DISABLED: 'neutral',
  BLOCKED: 'neutral',
};

/** Lanes still being worked — anything else renders the "all set" facts mode. */
const OPEN_LANES: ReadonlySet<CareLane> = new Set([
  'FAILED',
  'NEEDS_ISSUE',
  'ATTENTION',
  'BLOCKED_KYC',
]);

/** Statuses a top-up can land on (a live spendable card). */
const TOP_UP_CARD_STATUSES = new Set(['ACTIVE', 'ISSUED']);

export function CareResolvePanel({
  desk,
  disruptionUrn,
  board,
}: {
  desk: CareDeskRow;
  disruptionUrn: string;
  board: CareBoard | null;
}) {
  const { t } = useTranslation('care');
  const { row, card, lane } = desk;
  // The resolver's reason in words — an unrecognised one is not shown (never raw English).
  const policyWhy = policyReasonText(row.policyMatchReason, (k) => String(t(k)));
  /** K6: the ladder opens the formal outcome dialog with its rung pre-selected. */
  const [outcomePath, setOutcomePath] = useState<CareOutcomeKind | null>(null);
  /** K6: the top-up dialog — `prefill` set when opened from the suggested grant. */
  const [topUp, setTopUp] = useState<{ prefill?: CareTopUpPrefill } | null>(null);
  /** #808: the counter marks the coupon consumed (opens with the row's own reference). */
  const [redeemOpen, setRedeemOpen] = useState(false);

  const issue = useIssueGroupVoucher({ kind: 'disruption', disruptionUrn });

  // The lock is implicit on operation-intent — a 409 here means ANOTHER operator
  // holds this PNR; name them instead of failing anonymously.
  const conflict = issue.error instanceof LockConflictError ? issue.error : null;
  const plainError = !conflict && issue.isError ? t('resolve.writeFailed') : null;

  const walletKnown = board?.known.wallet !== false;
  const badge = LANE_BADGE[lane];
  const canIssue = lane === 'NEEDS_ISSUE' || lane === 'FAILED';
  // A top-up needs a live spendable card (the wallet 409s otherwise).
  const cardLive = card ? TOP_UP_CARD_STATUSES.has(card.status) : row.mealVoucher?.issued === true;
  const canTopUp = (lane === 'ISSUED' || lane === 'ATTENTION') && cardLive;
  // #808: a live card whose benefit is not consumed yet can be marked at the counter;
  // the cases denorm answers when the board predates the benefit field.
  const consumed = card?.benefit === 'REDEEMED' || (!card?.benefit && !!row.mealVoucher?.redeemedAt);
  const redemptionSource = card?.redemption?.source ?? row.mealVoucher?.redemptionSource;
  const canRedeem = cardLive && !consumed && card?.benefit !== 'VOIDED';
  const reason = careLaneReason(desk);

  return (
    <section
      className="flex min-h-0 flex-col rounded-lg border border-border bg-card"
      data-testid="care-resolve-panel"
      aria-label={t('resolve.ariaLabel', { pnr: row.pnrLabel })}
    >
      {/* Who + why — the context the actions below act on. */}
      <header className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-lg font-bold">{row.pnrLabel}</span>
          <PnrSourceMark source={row.pnrSource} className="self-center" testId="care-resolve-source" />
          <span className="text-sm text-muted-foreground">
            {t('resolve.paxFlight', { pax: row.paxCount, flight: row.flightLabel })}
          </span>
          {badge ? (
            <Badge size="sm" variant={badge.variant} data-testid="care-lane-badge">
              {t(badge.labelKey)}
            </Badge>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {reason ? t(reason.key, { ...reason.params }) : null}
        </p>
        {(row.priorityTags ?? []).length > 0 ? (
          <p className="mt-1.5 flex flex-wrap gap-1.5">
            {(row.priorityTags ?? []).map((tag) => (
              <span
                key={tag}
                className="rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-medium text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"
              >
                {priorityLabel(tag, t)}
              </span>
            ))}
          </p>
        ) : null}
        {conflict ? (
          <p
            className="mt-2 rounded-md bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-800 dark:bg-rose-950/40 dark:text-rose-200"
            data-testid="care-lock-conflict"
          >
            {t('resolve.lockConflict', {
              holder: conflict.holder?.displayName ?? t('common:holds.lock.anotherOperator'),
            })}
          </p>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {/* The entitlement — what this group is owed, and under which policy. */}
        <div className="rounded-md border border-border px-3 py-2.5" data-testid="care-entitlement">
          <p className="text-sm font-medium">
            {row.mealVoucher
              ? t('resolve.entitlement.voucher', {
                  amount: formatEntitlement(
                    row.mealVoucher.amountUsd,
                    row.mealVoucher.currency,
                  ),
                })
              : row.accommodationOutcome?.outcome === 'AIRPORT_WAIT'
                ? t('resolve.entitlement.airportWait')
                : t('resolve.entitlement.generic')}
            {row.mealVoucher?.includesLounge ? (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {t('resolve.entitlement.lounge')}
              </span>
            ) : null}
          </p>
          {row.governingPolicyUrn ? (
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid="care-policy-provenance">
              {t('resolve.entitlement.policy')}{' '}
              <span className="font-mono">{row.governingPolicyUrn}</span>
              {policyWhy ? t('resolve.entitlement.policyReason', { reason: policyWhy }) : ''}
            </p>
          ) : null}
          {(row.passengers ?? []).length > 0 ? (
            <ul className="mt-2 space-y-0.5">
              {(row.passengers ?? []).map((p) => (
                <li key={p.paxUrn} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span>{p.fullName}</span>
                  {(p.specialNeeds ?? []).map((code) => (
                    <span
                      key={code}
                      className="rounded bg-muted px-1 py-px text-[10px] font-semibold uppercase text-muted-foreground"
                    >
                      {code}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {/* The card — the wallet's honest truth. Absent balances stay absent. */}
        {card && !['NOT_ISSUED', 'BLOCKED_KYC'].includes(card.status) ? (
          <div
            className="rounded-md border border-border px-3 py-2.5"
            data-testid="care-card-strip"
          >
            <div className="flex flex-wrap items-center gap-2">
              <CreditCard className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="font-mono text-sm font-semibold">
                •••• {card.last4 ?? '····'}
              </span>
              <Badge size="sm" variant={CARD_STATUS_VARIANT[card.status] ?? 'muted'}>
                {card.status}
              </Badge>
              {card.pendingTopUp ? (
                <span
                  className="rounded bg-sky-100 px-1.5 py-0.5 text-[11px] font-medium text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
                  data-testid="care-settling-chip"
                >
                  {t('settling')}
                </span>
              ) : null}
              {consumed ? (
                <span
                  className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                  data-testid="care-redeemed-chip"
                >
                  <CheckCircle2 className="h-3 w-3" aria-hidden />
                  {redemptionSource === 'ISSUER' ? t('benefit.redeemedIssuer') : t('benefit.redeemedSimulated')}
                </span>
              ) : null}
              {card.reference ? (
                <span className="font-mono text-[11px] text-muted-foreground" data-testid="care-card-reference">
                  {t('benefit.reference', { reference: card.reference })}
                </span>
              ) : null}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground" data-testid="care-card-balance">
              {!walletKnown || card.availableMinor === undefined
                ? t('resolve.card.notRead')
                : t('resolve.card.available', {
                    available: formatMinor(card.availableMinor, card.currency),
                    total: formatMinor(
                      card.faceValueMinor + card.toppedUpMinor,
                      card.currency,
                    ),
                  })}
              {card.toppedUpMinor > 0
                ? t('resolve.card.toppedUp', {
                    amount: formatMinor(card.toppedUpMinor, card.currency),
                  })
                : ''}
            </p>
          </div>
        ) : null}

        {/* The KYC block — why nothing can be issued, and where the path goes. */}
        {lane === 'BLOCKED_KYC' ? (
          <div
            className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2.5 dark:border-sky-800 dark:bg-sky-950/30"
            data-testid="care-kyc-block"
          >
            <p className="text-sm font-semibold text-sky-800 dark:text-sky-200">
              {t('resolve.kyc.title')}
            </p>
            <p className="mt-1 text-xs leading-5 text-sky-800/80 dark:text-sky-200/80">
              {t('resolve.kyc.body')}
            </p>
          </div>
        ) : null}

        {/* Recorded outcome — read-only once declared. */}
        {lane === 'HANDLED_OUTSIDE' && row.careOutcome ? (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5 text-sm" data-testid="care-outcome-record">
            <p className="font-medium">
              {row.careOutcome.outcome === 'COUNTER_VOUCHER'
                ? t('resolve.outcome.counterVoucher')
                : t('resolve.outcome.other')}
            </p>
            {row.careOutcome.note ? (
              <p className="mt-1 text-xs text-muted-foreground">{row.careOutcome.note}</p>
            ) : null}
          </div>
        ) : null}

        {/* The suggested grant (K6): opens the top-up dialog PRE-FILLED — amount
            from the wait-window derivation, airport-wait reason templated. */}
        {row.careSuggestedGrant ? (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!cardLive}
              title={
                cardLive
                  ? t('resolve.suggested.titleReady')
                  : t('resolve.suggested.titleNoCard')
              }
              onClick={() =>
                setTopUp({
                  prefill: {
                    amount: row.careSuggestedGrant!.amount,
                    reasonCode: 'AIRPORT_WAIT_MEAL',
                    reason: t('resolve.suggested.reason'),
                  },
                })
              }
              data-testid="care-suggested-grant"
              className="border-violet-300 text-violet-700 dark:border-violet-800 dark:text-violet-300"
            >
              <Plus className="mr-1 h-3 w-3" aria-hidden />
              {t('resolve.suggested.label', {
                amount: formatMinor(
                  row.careSuggestedGrant.amount * 100,
                  row.careSuggestedGrant.currency,
                ),
              })}
            </Button>
          </div>
        ) : null}

        {/* The primary — issue / retry through the EXISTING per-group voucher seam. */}
        {canIssue ? (
          <Button
            onClick={() =>
              issue.mutate({
                subCaseUrn: row.subCaseUrn,
                caseUrn: row.caseUrn,
                disruptionUrn,
              })
            }
            disabled={issue.isPending}
            data-testid="care-issue"
          >
            {issue.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
            {lane === 'FAILED' ? t('resolve.issue.retry') : t('resolve.issue.issue')}
          </Button>
        ) : null}

        {/* The primary on a settled/assist row — the group's card can still be fed. */}
        {canTopUp ? (
          <Button onClick={() => setTopUp({})} data-testid="care-top-up-open">
            <Plus className="mr-1 h-3 w-3" aria-hidden />
            {t('resolve.topUpOpen')}
          </Button>
        ) : null}
        {/* #808: the counter marks the coupon consumed (validated by printed reference). */}
        {canRedeem ? (
          <Button variant="outline" onClick={() => setRedeemOpen(true)} data-testid="care-redeem-open">
            <CheckCircle2 className="mr-1 h-3 w-3" aria-hidden />
            {t('redeem.open')}
          </Button>
        ) : null}

        {/* A row in no open lane (counter walk-up / deep link): read-only facts. */}
        {!OPEN_LANES.has(lane) ? (
          <p className="text-xs text-muted-foreground" data-testid="care-all-set">
            {t('resolve.allSet')}
          </p>
        ) : null}
        {plainError ? <p className="text-xs text-destructive">{plainError}</p> : null}
      </div>

      {/* The alternatives ladder — once, with context, quiet until needed. Each
          rung opens the formal outcome dialog (K6) with its path pre-selected. */}
      <footer className="border-t border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-xs text-muted-foreground">
            {t('resolve.alternatives')}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOutcomePath('COUNTER_VOUCHER')}
            title={t('resolve.counterVoucher.title')}
            data-testid="care-counter-voucher"
          >
            {t('resolve.counterVoucher.label')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOutcomePath('HANDLED_OUTSIDE')}
            className={cn('text-muted-foreground hover:text-rose-600')}
            title={t('resolve.handledOutside.title')}
            data-testid="care-handled-outside"
          >
            {t('resolve.handledOutside.label')}
          </Button>
        </div>
      </footer>

      {outcomePath ? (
        <CareOutcomeDialog
          desk={desk}
          disruptionUrn={disruptionUrn}
          initialPath={outcomePath}
          onClose={() => setOutcomePath(null)}
        />
      ) : null}
      {topUp ? (
        <CareTopUpDialog
          desk={desk}
          disruptionUrn={disruptionUrn}
          board={board}
          {...(topUp.prefill ? { prefill: topUp.prefill } : {})}
          onClose={() => setTopUp(null)}
        />
      ) : null}
      {redeemOpen ? (
        <CareRedeemDialog
          card={{
            subCaseUrn: row.subCaseUrn,
            pnrLabel: row.pnrLabel,
            holderMasked: card?.holderMasked ?? row.leadName ?? '',
            currency: card?.currency ?? row.mealVoucher?.currency ?? 'USD',
            ...(card?.reference ? { reference: card.reference } : {}),
          }}
          caseUrn={row.caseUrn}
          disruptionUrn={disruptionUrn}
          {...(card?.reference ? { prefillReference: card.reference } : {})}
          onClose={() => setRedeemOpen(false)}
        />
      ) : null}
    </section>
  );
}
